import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FaChevronDown } from "react-icons/fa6";
import { ownerSelector } from "../../store/store";
import TagSwatch from "./TagSwatch";

type TagsHeaderMenuProps = {
  onCreateNew: () => void;
  selectedTagId?: string;
  onSelectTag?: (tagId: string) => void;
};

const TagsHeaderMenu: React.FC<TagsHeaderMenuProps> = ({
  onCreateNew,
  selectedTagId = "all",
  onSelectTag,
}) => {
  const { tags } = useSelector(ownerSelector);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedTag = tags.find((tag) => tag._id === selectedTagId);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className="relative min-w-[200px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative h-9 w-full rounded-[8px] border border-light-grey bg-white px-4 pr-10 text-left text-sm text-black transition-colors duration-200 hover:border-brand-blue"
      >
        <span className="block truncate">{selectedTag ? selectedTag.name : "Tags"}</span>
        <FaChevronDown
          size={12}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 shrink-0 text-dark-grey"
        />
      </button>
      {open ? (
        <div className="absolute left-0 top-[calc(100%+6px)] z-[80] w-[220px] overflow-hidden rounded-[8px] border border-light-grey bg-white shadow-lg">
          <div className="bg-primary px-3 py-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-dark-grey">Tags</p>
          </div>
          <div className="max-h-[220px] overflow-y-auto slimScrollbar">
            {tags.length === 0 ? (
              <p className="px-3 py-3 text-xs text-dark-grey">No tags yet</p>
            ) : (
              <ul className="py-1">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      onSelectTag?.("all");
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${
                      selectedTagId === "all"
                        ? "bg-primary font-medium text-dark-black"
                        : "text-dark-black hover:bg-primary"
                    }`}
                  >
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-light-grey bg-white" />
                    <span className="min-w-0 truncate">All tags</span>
                  </button>
                </li>
                {tags.map((tag) => (
                  <li key={tag._id}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectTag?.(tag._id);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${
                        selectedTagId === tag._id
                          ? "bg-primary font-medium text-dark-black"
                          : "text-dark-black hover:bg-primary"
                      }`}
                    >
                      <TagSwatch color={tag.color} size="sm" />
                      <span className="min-w-0 truncate">{tag.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-light-grey bg-primary px-2 py-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onCreateNew();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-[6px] py-2 text-xs font-medium text-dark-black hover:bg-white/60"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-dark-grey text-[12px] leading-none">
                +
              </span>
              Create New
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TagsHeaderMenu;
